import { useState, useRef } from 'react';
import { Button, juice, sfx } from '../ui';
import './GuessInput.css';

/* ============================================================================
   GuessInput — the terminal prompt the whole game runs through.

   Feedback is fired from here rather than from Game because this component
   knows where on screen the interaction happened: bursts and floating text are
   anchored to the field itself, so the reaction reads as coming from the thing
   the player just touched.
   ========================================================================== */

const REACTIONS = {
  correct: {
    sound: 'correct',
    shake: 'light',
    palette: 'gold',
    float: 'HIT',
    tone: 'lime',
    count: 22,
    flash: null,
  },
  incorrect: {
    sound: 'wrong',
    shake: 'medium',
    palette: 'fire',
    float: 'MISS',
    tone: 'red',
    count: 10,
    flash: 'rgba(255, 31, 61, 0.2)',
  },
  duplicate: {
    sound: 'duplicate',
    shake: 'light',
    palette: 'cyan',
    float: 'ALREADY TRIED',
    tone: 'cyan',
    count: 6,
    flash: null,
  },
  close: {
    sound: 'close',
    shake: 'light',
    palette: 'gold',
    float: 'WARM',
    tone: 'gold',
    count: 14,
    flash: null,
  },
};

function GuessInput({ onGuess, feedback, disabled = false }) {
  const [input, setInput] = useState('');
  const formRef = useRef(null);

  const handleSubmit = (event) => {
    event.preventDefault();
    const word = input.trim();
    if (!word || disabled) return;

    const result = onGuess(word);
    setInput('');

    const reaction = REACTIONS[result];
    if (!reaction || !formRef.current) return;

    const rect = formRef.current.getBoundingClientRect();
    const originX = rect.left + rect.width / 2;
    const originY = rect.top + rect.height / 2;

    sfx.play(reaction.sound);
    juice.shake(reaction.shake);
    juice.burst({
      x: originX,
      y: originY,
      count: reaction.count,
      palette: reaction.palette,
      power: result === 'correct' ? 1.2 : 0.75,
    });
    juice.float({
      text: reaction.float,
      x: originX,
      y: rect.top - 6,
      tone: reaction.tone,
    });
    if (reaction.flash) juice.flash(reaction.flash, 240);
  };

  return (
    <form
      ref={formRef}
      className={`guess ${feedback ? `guess--${feedback}` : ''}`}
      onSubmit={handleSubmit}
    >
      <div className="guess__field">
        <span className="guess__prompt" aria-hidden="true">
          &gt;
        </span>
        <input
          type="text"
          value={input}
          onChange={(event) => setInput(event.target.value)}
          placeholder="type a word"
          className="guess__input"
          aria-label="Guess a word"
          autoComplete="off"
          autoCorrect="off"
          spellCheck="false"
          disabled={disabled}
          autoFocus
        />
      </div>

      <Button type="submit" variant="primary" size="lg" disabled={disabled} sound={null}>
        Guess
      </Button>
    </form>
  );
}

export default GuessInput;
